import { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Users, Map, Star, Calendar } from 'lucide-react';

const stats = [
  { 
    icon: Users, 
    value: 500, 
    suffix: '+',
    label: 'Turistas Felices',
    color: '#FF6B4A'
  },
  { 
    icon: Map, 
    value: 50, 
    suffix: '+',
    label: 'Excursiones Realizadas',
    color: '#00A896'
  },
  { 
    icon: Star, 
    value: 4.9, 
    suffix: '',
    label: 'Rating Promedio',
    color: '#FF6B4A',
    decimals: 1
  },
  { 
    icon: Calendar, 
    value: 5, 
    suffix: '+',
    label: 'Años de Experiencia',
    color: '#00A896'
  }
];

const CountUp = ({ end, duration = 2, decimals = 0, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (!isInView) return;

    let startTime;
    const startValue = 0;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = startValue + (end - startValue) * easeOutQuart;
      
      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isInView, end, duration]);

  return (
    <span ref={ref}>
      {decimals > 0 ? count.toFixed(decimals) : Math.floor(count)}{suffix}
    </span>
  );
};

export const StatsCounter = () => {
  return (
    <section className="py-16 md:py-20 bg-[#012A36] relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-64 h-64 rounded-full bg-[#00A896] blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-[#FF6B4A] blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="text-center"
            >
              {/* Icon */}
              <motion.div 
                className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: `${stat.color}20` }}
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <stat.icon className="w-8 h-8" style={{ color: stat.color }} />
              </motion.div>

              {/* Number */}
              <div 
                className="text-4xl md:text-5xl font-bold mb-2"
                style={{ color: stat.color }}
              >
                <CountUp 
                  end={stat.value} 
                  decimals={stat.decimals || 0}
                  suffix={stat.suffix}
                />
              </div>

              {/* Label */}
              <p className="text-white/70 text-sm md:text-base font-medium">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsCounter;
